// Every route of MG AI School (ARCHITECTURE section 15). Slices replace page bodies, never this table.
// Platform pages are data router lazy routes. Community pages are descendant routes under /:slug/*
// (see CommunityRoutes) so a post or the settings modal can render over the page in
// location.state.backgroundLocation. Reserved slugs never match a community: they render the 404 page.
import { lazy } from 'react'
import { createBrowserRouter, type RouteObject } from 'react-router'
import { AppShell } from '@/components/layout/AppShell'
import { BareLayout } from '@/components/layout/BareLayout'
import { CommunityRoutes, LazyPage, LiveRoute } from '@/components/layout/CommunityRoutes'
import { PageSkeleton } from '@/components/layout/PageSkeleton'
import { RootLayout } from '@/components/layout/RootLayout'
import type { CommunityPageDef, PageLoader, RouteHandle } from '@/components/layout/types'
import NotFoundPage from '@/features/errors/pages/NotFoundPage'
import RouteErrorPage from '@/features/errors/pages/RouteErrorPage'
import { RequireAuth, RequirePlatformAdmin } from '@/lib/auth'

/** Data router lazy route from a page module with a default export. */
const page = (load: PageLoader) => async () => ({ Component: (await load()).default })

/** Community pages, relative to /:slug (the index route is the feed). */
export const communityPages: CommunityPageDef[] = [
  { path: '', Page: lazy(() => import('@/features/feed/pages/FeedPage')) },
  { path: 'post/:postSlug', Page: lazy(() => import('@/features/feed/pages/PostPage')), modal: true },
  { path: 'classroom', Page: lazy(() => import('@/features/classroom/pages/ClassroomPage')), fullWidth: true },
  { path: 'classroom/:courseId', Page: lazy(() => import('@/features/classroom/pages/CoursePage')), fullWidth: true },
  { path: 'calendar', Page: lazy(() => import('@/features/calendar/pages/CalendarPage')), fullWidth: true },
  { path: 'members', Page: lazy(() => import('@/features/members/pages/MembersPage')) },
  { path: 'map', Page: lazy(() => import('@/features/members/pages/MapPage')), fullWidth: true },
  { path: 'leaderboards', Page: lazy(() => import('@/features/leaderboards/pages/LeaderboardsPage')), fullWidth: true },
  { path: 'about', Page: lazy(() => import('@/features/communities/pages/AboutPage')), headerCard: false },
  { path: 'plans', Page: lazy(() => import('@/features/billing/pages/PlansPage')), headerCard: false },
  { path: 'affiliates', Page: lazy(() => import('@/features/affiliates/pages/AffiliatesPage')) },
  { path: 'search', Page: lazy(() => import('@/features/search/pages/SearchPage')) },
  {
    path: 'settings/:section?',
    Page: lazy(() => import('@/features/community-settings/pages/CommunitySettingsPage')),
    modal: true,
    fullWidth: true,
  },
]


// The dev only UI gallery (F1). import.meta.glob tolerates the file not existing.
const galleryModules = import.meta.env.DEV ? import.meta.glob<{ default: never }>('./dev/UiGallery.tsx') : {}
const galleryLoader = galleryModules['./dev/UiGallery.tsx'] as PageLoader | undefined
const devRoutes: RouteObject[] = galleryLoader ? [{ path: '_ui', lazy: page(galleryLoader) }] : []

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    hydrateFallbackElement: <PageSkeleton variant="blank" />,
    children: [
      ...devRoutes,
      // Full screen live call, no top bar.
      { path: ':slug/live/:roomId', element: <LiveRoute Page={lazy(() => import('@/features/live/pages/LiveRoomPage'))} /> },
      // Auth screens and printable certificates: banners only, no top bar.
      {
        element: <BareLayout />,
        children: [
          { path: 'login', lazy: page(() => import('@/features/auth/pages/LoginPage')) },
          { path: 'signup', lazy: page(() => import('@/features/auth/pages/SignupPage')) },
          { path: 'forgot-password', lazy: page(() => import('@/features/auth/pages/ForgotPasswordPage')) },
          { path: 'reset-password', lazy: page(() => import('@/features/auth/pages/ResetPasswordPage')) },
          { path: 'verify-email', lazy: page(() => import('@/features/auth/pages/VerifyEmailPage')) },
          { path: 'certificates/:code', lazy: page(() => import('@/features/classroom/pages/CertificatePage')) },
          { path: 'unsubscribe', lazy: page(() => import('@/features/notifications/pages/UnsubscribePage')) },
        ],
      },
      // Everything else lives in the app shell (top bar, banners, bottom nav).
      {
        element: <AppShell />,
        children: [
          { index: true, lazy: page(() => import('@/features/discover/pages/HomePage')) },
          { path: 'discover', lazy: page(() => import('@/features/discover/pages/DiscoverPage')) },
          { path: 'u/:handle', lazy: page(() => import('@/features/profile/pages/ProfilePage')) },
          { path: '_fake/checkout/:id', lazy: page(() => import('@/features/billing/pages/FakeCheckoutPage')) },
          { path: 'terms', lazy: page(() => import('@/features/legal/pages/TermsPage')) },
          { path: 'privacy', lazy: page(() => import('@/features/legal/pages/PrivacyPage')) },
          { path: 'support', lazy: page(() => import('@/features/legal/pages/SupportPage')) },
          // G-18: accessibility statement (WCAG 2.2 AA target, known limitations, contact)
          { path: 'accessibility', lazy: page(() => import('@/features/legal/pages/AccessibilityPage')) },
          // MG's short link mgai.school/resources: redirects to the Resource Library lesson (platform status resourcesPath).
          { path: 'resources', lazy: page(() => import('@/features/discover/pages/ResourcesPage')) },
          // REDESIGN 1 (Q-TRUST-08): platform admins only; the page itself sends everyone else, visitors too, to "/".
          { path: 'create', lazy: page(() => import('@/features/communities/pages/CreateCommunityPage')) },
          {
            element: <RequireAuth />,
            children: [
              { path: 'settings/:section?', lazy: page(() => import('@/features/account/pages/UserSettingsPage')) },
              {
                path: 'chat/:conversationId?',
                // A fixed height screen: no page footer under it (Q-UX-38).
                handle: { fullHeight: true } satisfies RouteHandle,
                lazy: page(() => import('@/features/chat/pages/ChatPage')),
              },
              { path: 'notifications', lazy: page(() => import('@/features/notifications/pages/NotificationsPage')) },
              // G-26: a moderation decision and its appeal (linked from the moderation_action notice)
              { path: 'appeals/:id', lazy: page(() => import('@/features/appeals/pages/AppealPage')) },
            ],
          },
          {
            // Super admin console (platform_role admin). It renders its own routes for /admin/<section>.
            path: 'admin/*',
            element: (
              <RequirePlatformAdmin fallback={<NotFoundPage />}>
                <LazyPage Page={lazy(() => import('@/features/admin/pages/AdminConsolePage'))} fallback={<PageSkeleton variant="page" />} />
              </RequirePlatformAdmin>
            ),
          },
          { path: ':slug/*', element: <CommunityRoutes pages={communityPages} /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]

export const router = createBrowserRouter(routes)
